import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  AnalyticsOverviewResponse,
  AnalyticsOverviewResponseSchema,
  TopLineItem,
  TopLineItemsResponseSchema,
  ViewsSummaryResponse,
  ViewsSummaryResponseSchema,
  AnalyticsQuoteView,
  QuoteViewsResponseSchema,
} from "@forgepoint/shared";

interface QuoteViewState {
  isLoading: boolean;
  error: string | null;
}

interface AnalyticsStore {
  overview: AnalyticsOverviewResponse | null;
  overviewLoading: boolean;
  overviewError: string | null;
  topItems: TopLineItem[];
  topItemsLoading: boolean;
  topItemsError: string | null;
  viewsSummary: ViewsSummaryResponse | null;
  viewsSummaryLoading: boolean;
  viewsSummaryError: string | null;
  quoteViews: Map<string, AnalyticsQuoteView[]>;
  quoteViewStates: Map<string, QuoteViewState>;
  fetchOverview: () => Promise<void>;
  fetchTopItems: () => Promise<void>;
  fetchViewsSummary: () => Promise<void>;
  fetchQuoteViews: (quoteId: string) => Promise<void>;
}

const getInitialQuoteViewState = (): QuoteViewState => ({
  isLoading: false,
  error: null,
});

export const useAnalyticsStore = create<AnalyticsStore>((set) => ({
  overview: null,
  overviewLoading: false,
  overviewError: null,
  topItems: [],
  topItemsLoading: false,
  topItemsError: null,
  viewsSummary: null,
  viewsSummaryLoading: false,
  viewsSummaryError: null,
  quoteViews: new Map(),
  quoteViewStates: new Map(),

  fetchOverview: async () => {
    set({ overviewLoading: true, overviewError: null });
    try {
      const response = await fetchApi("analytics/overview", "GET", {
        params: { months: "6" },
      });
      if (!response.ok) {
        throw new Error("Failed to load overview");
      }
      const data = AnalyticsOverviewResponseSchema.parse(response.data);
      set({ overview: data, overviewLoading: false });
    } catch (error) {
      captureException(error as Error);
      set({
        overviewLoading: false,
        overviewError: "Unable to load overview metrics",
      });
    }
  },

  fetchTopItems: async () => {
    set({ topItemsLoading: true, topItemsError: null });
    try {
      const response = await fetchApi("analytics/top-items", "GET", {
        params: { months: "3", limit: "8" },
      });
      if (!response.ok) {
        throw new Error("Failed to load top items");
      }
      const data = TopLineItemsResponseSchema.parse(response.data);
      set({ topItems: data.items, topItemsLoading: false });
    } catch (error) {
      captureException(error as Error);
      set({
        topItemsLoading: false,
        topItemsError: "Unable to load top items",
      });
    }
  },

  fetchViewsSummary: async () => {
    set({ viewsSummaryLoading: true, viewsSummaryError: null });
    try {
      const response = await fetchApi("analytics/views/summary", "GET", {
        params: { weeks: "12", days: "30", limit: "10" },
      });
      if (!response.ok) {
        throw new Error("Failed to load views summary");
      }
      const data = ViewsSummaryResponseSchema.parse(response.data);
      set({ viewsSummary: data, viewsSummaryLoading: false });
    } catch (error) {
      captureException(error as Error);
      set({
        viewsSummaryLoading: false,
        viewsSummaryError: "Unable to load views summary",
      });
    }
  },

  fetchQuoteViews: async (quoteId: string) => {
    set((state) => {
      const quoteViewStates = new Map(state.quoteViewStates);
      const currentState =
        quoteViewStates.get(quoteId) || getInitialQuoteViewState();
      quoteViewStates.set(quoteId, {
        ...currentState,
        isLoading: true,
        error: null,
      });
      return { quoteViewStates };
    });

    try {
      const response = await fetchApi(
        `analytics/quotes/${quoteId}/views`,
        "GET",
      );
      if (!response.ok) {
        throw new Error("Failed to load quote views");
      }
      const data = QuoteViewsResponseSchema.parse(response.data);
      set((state) => {
        const quoteViews = new Map(state.quoteViews);
        const quoteViewStates = new Map(state.quoteViewStates);
        quoteViews.set(quoteId, data.views);
        quoteViewStates.set(quoteId, { isLoading: false, error: null });
        return { quoteViews, quoteViewStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const quoteViewStates = new Map(state.quoteViewStates);
        quoteViewStates.set(quoteId, {
          isLoading: false,
          error: "Unable to load quote views",
        });
        return { quoteViewStates };
      });
    }
  },
}));
